import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";

type ManagedUser = {
  id: string;
  email: string;
  name: string | null;
  avatarUrl: string | null;
  role: "admin" | "editor" | "viewer";
  editPermissions: string | null; // JSON string from API, or null
  createdAt: string;
};

const EDIT_SCOPES = ["timeline", "scenes", "characters", "storyboard"] as const;

type Props = {
  onBack: () => void;
};

export function Admin({ onBack }: Props) {
  const { user } = useAuth();
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"editor" | "viewer" | "admin">("editor");
  const [isInviting, setIsInviting] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await fetch("/api/admin/users", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch users");
      const data = await res.json();
      setUsers(data.users);
    } catch (err) {
      console.error("Failed to fetch users:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const showMessage = (type: "success" | "error", text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 4000);
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;

    setIsInviting(true);
    try {
      const res = await fetch("/api/admin/invite", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: inviteEmail, role: inviteRole }),
      });

      const data = await res.json();

      if (!res.ok) {
        showMessage("error", data.error || "Failed to send invite");
        return;
      }

      showMessage("success", `Invitation sent to ${inviteEmail}`);
      setInviteEmail("");
    } catch {
      showMessage("error", "Failed to send invite");
    } finally {
      setIsInviting(false);
    }
  };

  const handleRoleChange = async (userId: string, newRole: string) => {
    try {
      const res = await fetch(`/api/admin/users/${userId}/role`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ role: newRole }),
      });

      const data = await res.json();

      if (!res.ok) {
        showMessage("error", data.error || "Failed to update role");
        return;
      }

      setUsers((prev) =>
        prev.map((u) =>
          u.id === userId ? { ...u, role: newRole as ManagedUser["role"] } : u
        )
      );
      showMessage("success", "Role updated");
    } catch {
      showMessage("error", "Failed to update role");
    }
  };

  const getUserScopes = (u: ManagedUser): string[] | null => {
    if (!u.editPermissions) return null;
    try {
      return JSON.parse(u.editPermissions);
    } catch {
      return null;
    }
  };

  const handleToggleScope = async (userId: string, scope: string) => {
    const targetUser = users.find((u) => u.id === userId);
    if (!targetUser) return;

    const currentScopes = getUserScopes(targetUser);
    let newScopes: string[] | null;

    if (currentScopes === null) {
      // Currently has all permissions, removing one means we need to specify the rest
      newScopes = EDIT_SCOPES.filter((s) => s !== scope) as string[];
    } else if (currentScopes.includes(scope)) {
      // Remove scope
      newScopes = currentScopes.filter((s) => s !== scope);
      if (newScopes.length === 0) newScopes = [];
    } else {
      // Add scope
      newScopes = [...currentScopes, scope];
      // If all scopes selected, set to null (all)
      if (EDIT_SCOPES.every((s) => newScopes!.includes(s))) {
        newScopes = null;
      }
    }

    try {
      const res = await fetch(`/api/admin/users/${userId}/role`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          role: targetUser.role,
          editPermissions: newScopes,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        showMessage("error", data.error || "Failed to update permissions");
        return;
      }

      setUsers((prev) =>
        prev.map((u) =>
          u.id === userId
            ? { ...u, editPermissions: newScopes ? JSON.stringify(newScopes) : null }
            : u
        )
      );
    } catch {
      showMessage("error", "Failed to update permissions");
    }
  };

  const handleDelete = async (userId: string, email: string) => {
    if (!confirm(`Remove ${email}? This cannot be undone.`)) return;

    try {
      const res = await fetch(`/api/admin/users/${userId}`, {
        method: "DELETE",
        credentials: "include",
      });

      const data = await res.json();

      if (!res.ok) {
        showMessage("error", data.error || "Failed to remove user");
        return;
      }

      setUsers((prev) => prev.filter((u) => u.id !== userId));
      showMessage("success", `Removed ${email}`);
    } catch {
      showMessage("error", "Failed to remove user");
    }
  };

  const roleColors: Record<string, string> = {
    admin: "bg-primary-500/10 text-primary-400 border-primary-500/20",
    editor: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    viewer: "bg-surface text-muted border-border",
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <button
                onClick={onBack}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <span className="text-foreground font-medium text-sm">User Management</span>
            </div>
            <span className="text-muted text-sm">{user?.email}</span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {message && (
          <div
            className={`mb-6 p-3 rounded text-sm border ${
              message.type === "success"
                ? "bg-green-500/10 border-green-500/20 text-green-400"
                : "bg-red-500/10 border-red-500/20 text-red-400"
            }`}
          >
            {message.text}
          </div>
        )}

        {/* Invite form */}
        <div className="bg-surface border border-border rounded-lg p-5 mb-8">
          <h2 className="text-foreground font-medium text-sm mb-4">Invite User</h2>
          <form onSubmit={handleInvite} className="flex gap-3">
            <input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="Email address"
              className="flex-1 bg-surface-elevated border border-border rounded-lg px-4 py-2 text-foreground placeholder:text-muted text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <select
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as typeof inviteRole)}
              className="bg-surface-elevated border border-border rounded-lg px-3 py-2 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="editor">Editor</option>
              <option value="viewer">Viewer</option>
              <option value="admin">Admin</option>
            </select>
            <button
              type="submit"
              disabled={isInviting || !inviteEmail.trim()}
              className="bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-2 px-5 rounded-lg transition-colors text-sm whitespace-nowrap"
            >
              {isInviting ? "Sending..." : "Send Invite"}
            </button>
          </form>
        </div>

        {/* User list */}
        <div className="bg-surface border border-border rounded-lg overflow-hidden">
          <div className="px-5 py-3 border-b border-border">
            <h2 className="text-foreground font-medium text-sm">
              Users ({users.length})
            </h2>
          </div>

          {isLoading ? (
            <div className="p-8 text-center">
              <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
              <p className="text-muted text-xs">Loading users...</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {users.map((u) => {
                const scopes = getUserScopes(u);
                const isScopeActive = (scope: string) =>
                  scopes === null || scopes.includes(scope);

                return (
                  <div key={u.id} className="px-5 py-3">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3 min-w-0">
                        {u.avatarUrl ? (
                          <img
                            src={u.avatarUrl}
                            alt=""
                            className="w-8 h-8 rounded-full flex-shrink-0"
                          />
                        ) : (
                          <div className="w-8 h-8 bg-surface-elevated rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-muted text-xs font-medium">
                              {(u.name?.[0] || u.email[0]).toUpperCase()}
                            </span>
                          </div>
                        )}
                        <div className="min-w-0">
                          <div className="text-foreground text-sm truncate">
                            {u.name || u.email}
                          </div>
                          {u.name && (
                            <div className="text-muted-foreground text-xs truncate">
                              {u.email}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-3 flex-shrink-0">
                        <span className="text-muted-foreground text-xs hidden sm:block">
                          {new Date(u.createdAt).toLocaleDateString()}
                        </span>

                        {u.id === user?.id ? (
                          <span
                            className={`px-2.5 py-0.5 rounded text-xs font-medium border ${roleColors[u.role]}`}
                          >
                            {u.role} (you)
                          </span>
                        ) : (
                          <>
                            <select
                              value={u.role}
                              onChange={(e) => handleRoleChange(u.id, e.target.value)}
                              className={`px-2.5 py-0.5 rounded text-xs font-medium border appearance-none cursor-pointer ${roleColors[u.role]}`}
                            >
                              <option value="admin">admin</option>
                              <option value="editor">editor</option>
                              <option value="viewer">viewer</option>
                            </select>

                            <button
                              onClick={() => handleDelete(u.id, u.email)}
                              className="text-muted-foreground hover:text-red-400 transition-colors p-1"
                              title="Remove user"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Permission chips for editors */}
                    {u.role === "editor" && u.id !== user?.id && (
                      <div className="mt-2 ml-11 flex items-center gap-1.5">
                        <span className="text-[10px] text-muted uppercase tracking-wider mr-1">Permissions:</span>
                        {scopes === null && (
                          <span className="text-[10px] text-primary-400 mr-1">All</span>
                        )}
                        {EDIT_SCOPES.map((scope) => (
                          <button
                            key={scope}
                            onClick={() => handleToggleScope(u.id, scope)}
                            className={`px-2 py-0.5 rounded text-[10px] font-medium border transition-colors ${
                              isScopeActive(scope)
                                ? "border-primary-500/40 bg-primary-500/10 text-primary-400"
                                : "border-border bg-surface text-muted hover:text-muted-foreground"
                            }`}
                          >
                            {scope}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
